import {useCallback} from 'react';
import {createSelector} from 'reselect';
import {useDesignerSelector} from '@packages/systems/designer/flux';
import {selectCMSCollections} from '@packages/systems/plugins/CMS';
import {useFeatureFlag} from '@packages/systems/feature-config/client';
import {FEATURE_FLAGS} from '@packages/systems/feature-config/shared';
import {useFirstTimeUse} from '@packages/systems/designer/apps/client';

/**
 * Persistent FTU key for the AICC CMS-picker onboarding popover. Namespaced
 * alongside the `AICC_CMS_PICKER` (`ff-ext-796`) feature flag so the callout
 * only fires once per user across all AICC composers (chat + creator).
 */
export const AICC_CMS_PICKER_ONBOARDING_FTU_ID =
  'AICC_CMS_PICKER_ONBOARDING_POPOVER_SHOWN';

// Derive a boolean so the composer re-renders only when the first collection
// appears, not on every collection/field edit that reslices the map.
const selectHasCmsCollection = createSelector(
  selectCMSCollections,
  (collections) => collections.size > 0
);

export interface UseCmsPickerOnboardingPopoverResult {
  /** Whether the onboarding popover should be visible right now. */
  shouldShowPopover: boolean;
  /** Permanently dismisses the FTU. Idempotent and guarded on eligibility. */
  dismiss: () => void;
}

/**
 * Drives the one-time onboarding popover that points at the AICC prompt-box
 * "+" icon and tells the user they can attach CMS Collection data. Gated on
 * the `AICC_CMS_PICKER` (`ff-ext-796`) flag so the callout is inert when the
 * picker itself is not available.
 */
export const useCmsPickerOnboardingPopover =
  (): UseCmsPickerOnboardingPopoverResult => {
    const cmsPickerEnabled = useFeatureFlag(FEATURE_FLAGS.AICC_CMS_PICKER);
    const hasCmsCollection = useDesignerSelector(selectHasCmsCollection);
    const [isFirstTime, hide] = useFirstTimeUse(
      AICC_CMS_PICKER_ONBOARDING_FTU_ID
    );

    // Require a collection to exist: with nothing to connect to, surfacing the
    // popover would be misleading.
    const shouldShowPopover =
      cmsPickerEnabled && hasCmsCollection && isFirstTime;

    const dismiss = useCallback(() => {
      // The "+" fires this on every open (via AddContentPopover.onOpened), so
      // guard on eligibility. Otherwise a pre-collection image-upload click
      // would burn the FTU before the onboarding could ever appear.
      if (!shouldShowPopover) return;
      hide();
    }, [shouldShowPopover, hide]);

    return {shouldShowPopover, dismiss};
  };
